// Service pour les statistiques personnelles (utilise maintenant les services API)
import { chatHistoryApiService } from './chatHistoryApi';
import { favoritesApiService } from './favoritesApi';

export interface UserStatistics {
  totalQuestions: number;
  totalInteractions: number;
  favoriteCategories: Array<{ category: string; count: number }>;
  mostUsedFeatures: Array<{ feature: string; count: number }>;
  averageResponseTime: number;
  totalFavorites: number;
  questionsByDay: Array<{ date: string; count: number }>;
}

class StatisticsService {
  // Obtenir les statistiques de l'utilisateur
  async getStatistics(): Promise<UserStatistics> {
    try {
      const [interactionsData, favorites] = await Promise.all([
        chatHistoryApiService.getAllInteractions(),
        favoritesApiService.getAllFavorites(),
      ]);

      const interactions = interactionsData.results || [];

      // Compter les questions
      const totalQuestions = interactions.length;

      // Catégories favorites
      const categoryCount: Record<string, number> = {};
      favorites.forEach((fav) => {
        const cat = fav.category || fav.type;
        categoryCount[cat] = (categoryCount[cat] || 0) + 1;
      });

      const favoriteCategories = Object.entries(categoryCount)
        .map(([category, count]) => ({ category, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      // Fonctionnalités les plus utilisées
      const featureCount: Record<string, number> = {};
      interactions.forEach((interaction) => {
        const feature = interaction.source || 'unknown';
        featureCount[feature] = (featureCount[feature] || 0) + 1;
      });

      const mostUsedFeatures = Object.entries(featureCount)
        .map(([feature, count]) => ({ feature, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      // Temps de réponse moyen
      const responseTimes = interactions
        .map((i) => i.responseTime)
        .filter((t): t is number => typeof t === 'number' && t > 0);
      const averageResponseTime =
        responseTimes.length > 0
          ? Math.round(responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length)
          : 0;

      // Questions par jour (7 derniers jours)
      const questionsByDay: Array<{ date: string; count: number }> = [];
      const last7Days = Array.from({ length: 7 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - i);
        return date.toISOString().split('T')[0];
      }).reverse();

      last7Days.forEach((date) => {
        const count = interactions.filter((i) => {
          // Valider que timestamp existe et est valide
          if (!i.timestamp) return false;
          try {
            const timestampDate = new Date(i.timestamp);
            // Vérifier si la date est valide
            if (isNaN(timestampDate.getTime())) return false;
            const interactionDate = timestampDate.toISOString().split('T')[0];
            return interactionDate === date;
          } catch (error) {
            console.warn('Date invalide dans interaction:', i.timestamp, error);
            return false;
          }
        }).length;
        questionsByDay.push({
          date: new Date(date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' }),
          count,
        });
      });

      return {
        totalQuestions,
        totalInteractions: interactions.length,
        favoriteCategories,
        mostUsedFeatures,
        averageResponseTime,
        totalFavorites: favorites.length,
        questionsByDay,
      };
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      // Retourner des statistiques vides en cas d'erreur
      return {
        totalQuestions: 0,
        totalInteractions: 0,
        favoriteCategories: [],
        mostUsedFeatures: [],
        averageResponseTime: 0,
        totalFavorites: 0,
        questionsByDay: [],
      };
    }
  }
}

export const statisticsService = new StatisticsService();
